import { faSave } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MultiSelect from 'components/common/MultiSelect';
import Editor from 'components/editor/Editor';
import PlaceholderAutocomplete from 'components/settings/PlaceholderAutocomplete';
import PlaceholderSelector from 'components/settings/PlaceholderSelector';
import { processHtmlForEmail } from 'helpers/utils';
import useNotification from 'hooks/useNotification';
import { useEffect, useRef, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import MessageTemplateService from 'services/MessageTemplate.service';

const typeOptions = [
  { value: 'NOTE', label: 'Nota' },
  { value: 'EMAIL', label: 'E-mail' },
  { value: 'APPOINTMENT', label: 'Apontamento' },
  { value: 'TICKET_DESCRIPTION', label: 'Descrição do Ticket' }
];

const MessageTemplateModal = ({
  showModal,
  handleClose,
  editingTemplate,
  onSuccess
}) => {
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const [validated, setValidated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contentHtml, setContentHtml] = useState('');

  const initialState = {
    name: '',
    content: '',
    types: []
  };

  const [body, setBody] = useState(initialState);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const form = useRef(null);
  const editorRef = useRef(null);
  const placeholderSelectorRef = useRef(null);

  useEffect(() => {
    if (editingTemplate) {
      const templateTypes = editingTemplate.types || [];
      const formattedTypes = templateTypes.map(type => {
        const option = typeOptions.find(opt => opt.value === type);
        return option || { value: type, label: type };
      });

      setBody({
        name: editingTemplate.name || '',
        content: editingTemplate.content || '',
        types: templateTypes
      });
      setSelectedTypes(formattedTypes);
      setContentHtml(editingTemplate.content || '');
    } else {
      setBody(initialState);
      setSelectedTypes([]);
      setContentHtml('');
    }
    setValidated(false);
  }, [editingTemplate, showModal]);

  // Listener para atalho de teclado Ctrl+Space e fechamento automático do dropdown
  useEffect(() => {
    if (!showModal) return;

    const handleKeyDown = e => {
      // Verifica se Ctrl+Space foi pressionado
      if (e.ctrlKey && e.code === 'Space') {
        const activeElement = document.activeElement;

        // Verifica se está dentro do modal
        const modalElement = document.querySelector('.modal.show');
        if (!modalElement || !modalElement.contains(activeElement)) {
          return;
        }

        // Verifica se o editor está focado
        const editorElement = document.querySelector('.ql-editor');
        const isEditorFocused =
          editorElement && editorElement === activeElement;

        // Só processa se o editor estiver focado
        if (isEditorFocused) {
          e.preventDefault();
          e.stopPropagation();

          if (placeholderSelectorRef.current) {
            // Se já está aberto, fecha; caso contrário, abre
            if (placeholderSelectorRef.current.isOpen()) {
              placeholderSelectorRef.current.close();
            } else {
              placeholderSelectorRef.current.open();
            }
          }
        }
      }
    };

    // Fecha o dropdown ao clicar em outro campo (mas não dentro do dropdown)
    const handleMouseDown = e => {
      if (!placeholderSelectorRef.current?.isOpen()) {
        return;
      }

      const target = e.target;
      const editorElement = document.querySelector('.ql-editor');
      const modalElement = document.querySelector('.modal.show');

      // Verifica se está dentro do modal
      if (!modalElement || !modalElement.contains(target)) {
        placeholderSelectorRef.current.close();
        return;
      }

      // Verifica se o clique foi dentro do dropdown ou no botão toggle
      const dropdownMenu = document.querySelector('.dropdown-menu');
      const dropdownToggle = document.querySelector('.placeholder-toggle-btn');
      const isInDropdownMenu =
        dropdownMenu &&
        (dropdownMenu.contains(target) ||
          target.closest('.dropdown-menu') ||
          target.closest('.dropdown'));
      const isToggleButton =
        dropdownToggle &&
        (target === dropdownToggle ||
          target.closest('.placeholder-toggle-btn'));

      // Se clicou dentro do dropdown ou no botão toggle, não fecha
      if (isInDropdownMenu || isToggleButton) {
        return;
      }

      // Se clicou em um input, textarea ou outro campo editável que não seja o editor
      const isInputField =
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT') &&
        target !== editorElement &&
        !editorElement?.contains(target);

      // Se clicou em outro campo editável, fecha
      if (isInputField) {
        placeholderSelectorRef.current.close();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [showModal]);

  const handleContentChange = html => {
    setContentHtml(html);
    setBody({ ...body, content: html });
  };

  const handleTypeChange = selectedOptions => {
    const types = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
    setSelectedTypes(selectedOptions || []);
    setBody({
      ...body,
      types
    });
  };

  const onSubmit = async e => {
    e.preventDefault();

    if (form.current.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    if (!body.types || body.types.length === 0) {
      showErrorNotification('Selecione pelo menos um tipo de modelo');
      setValidated(true);
      return;
    }

    setIsSubmitting(true);

    try {
      // Processa o HTML antes de enviar
      const processedContent = processHtmlForEmail(contentHtml || body.content);

      const payload = {
        ...body,
        content: processedContent
      };

      let response;
      if (editingTemplate) {
        response = await MessageTemplateService.update(
          editingTemplate.id,
          payload
        );
      } else {
        response = await MessageTemplateService.create(payload);
      }

      if (response.userMessage) {
        showErrorNotification(response.userMessage);
        setIsSubmitting(false);
        return;
      }

      showSuccessNotification(
        editingTemplate
          ? 'Modelo de mensagem editado com sucesso'
          : 'Modelo de mensagem criado com sucesso'
      );

      onSuccess();
    } catch (error) {
      showErrorNotification('Erro ao salvar modelo de mensagem');
    } finally {
      setIsSubmitting(false);
    }
  };

  const required = <span className="text-danger">*</span>;

  return (
    <Modal show={showModal} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {editingTemplate
            ? 'Editar Modelo de Mensagem'
            : 'Novo Modelo de Mensagem'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form validated={validated} ref={form}>
          <Form.Group className="mb-3">
            <Form.Label>Nome {required}</Form.Label>
            <Form.Control
              required
              type="text"
              placeholder="Nome do modelo de mensagem"
              value={body.name}
              onChange={e => setBody({ ...body, name: e.target.value })}
            />
            <Form.Control.Feedback type="invalid">
              Insira um nome.
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Tipos {required}</Form.Label>
            <MultiSelect
              placeholder="Selecione os tipos de modelo"
              options={typeOptions}
              value={selectedTypes}
              onChange={handleTypeChange}
              isSearchable={false}
            />
            {validated && (!body.types || body.types.length === 0) && (
              <div
                className="text-danger mt-1"
                style={{ fontSize: '0.875rem' }}
              >
                Selecione pelo menos um tipo.
              </div>
            )}
          </Form.Group>

          <Form.Group className="mb-1">
            <Form.Label className="d-flex align-items-center justify-content-between gap-2">
              <span>Conteúdo {required}</span>
              <div className="d-flex align-items-center gap-2">
                <PlaceholderSelector
                  ref={placeholderSelectorRef}
                  selectedTypes={body.types || []}
                  onInsertPlaceholder={placeholder => {
                    if (editorRef.current) {
                      editorRef.current.insertText(placeholder);
                    }
                  }}
                />
              </div>
            </Form.Label>
            <div className="position-relative">
              <Editor
                ref={editorRef}
                data={contentHtml}
                value={handleContentChange}
                theme="snow"
              />
              <PlaceholderAutocomplete
                editorRef={editorRef}
                selectedTypes={body.types || []}
                onInsertPlaceholder={placeholder => {
                  // Placeholder já foi inserido pelo autocomplete
                }}
              />
            </div>
            <div className="d-flex justify-content-end"></div>
            <Form.Control.Feedback type="invalid">
              Insira um conteúdo.
            </Form.Control.Feedback>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="falcon-default"
          onClick={handleClose}
          disabled={isSubmitting}
        >
          Fechar
        </Button>
        <Button variant="primary" onClick={onSubmit} disabled={isSubmitting}>
          <FontAwesomeIcon icon={faSave} className="me-2" />
          {isSubmitting ? 'Salvando...' : 'Salvar'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MessageTemplateModal;
